import paper from 'paper/dist/paper-core'
import { CycleBoundaryDrawable } from '../../paper'
import { createWallPath } from '../../utils/mesh/createWallPath'
import {
  detectOverlappingEdges,
  detectPathIntersections,
  detectSmallAnglesAtPath,
} from '../../utils/mesh/geometryValidation'
import { calculateOuterCornerPoint, intersectLines, SNAP_ANGLE_INTERVAL } from '../../utils'
import { CompoundPathWithEdgeMap } from '../edge/CompoundPathWithEdgeMap'
import { Edge } from '../edge/Edge'
import { PlanType } from '../PlanType'
import { Mesh } from './Mesh'
import { MeshPoint } from './MeshPoint'

export class WallMesh extends Mesh {
  public constructor(private readonly paperScope: paper.PaperScope) {
    super()
  }

  generatePath(): CompoundPathWithEdgeMap {
    return createWallPath(this)
  }

  public removeEdge(edge: Edge, skipPreserving = false): boolean {
    if (!skipPreserving) {
      const startCorner = calculateOuterCornerPoint(edge.startPoint)
      const endCorner = calculateOuterCornerPoint(edge.endPoint)
      if (startCorner) {
        edge.startPoint.set(resetAnchorPoint(edge.startPoint, edge, startCorner))
      }
      if (endCorner) {
        edge.endPoint.set(resetAnchorPoint(edge.endPoint, edge, endCorner))
      }
    }

    const pointsToDelete: MeshPoint[] = []
    let deletedAnEdge = false

    this.points.forEach((point) => {
      if (point.edges.has(edge)) {
        point.edges.delete(edge)
        deletedAnEdge = true

        if (point.edges.size === 0) {
          pointsToDelete.push(point)
        }
      }
    })

    pointsToDelete.forEach((point) => {
      this.points.delete(point)
    })

    return deletedAnEdge
  }

  public updateEdgeValidity(): number {
    const edges = [...this.getAllEdges()]
    edges.forEach((edge) => (edge.isValid = true))

    if (edges.length < 2) {
      return 0
    }

    /**
     * Edges that overlap are invalid and must
     * not be visited inside createWallPath.
     */
    const overlappingEdges = detectOverlappingEdges(edges)
    const compoundPath = createWallPath(this)
    const edgeMap = compoundPath.edgeMap

    const intersectedEdges = detectPathIntersections(compoundPath, edges)

    const smallPathAngleEdges = detectSmallAnglesAtPath(edgeMap, compoundPath, PlanType.WALL)

    const invalidEdges = new Set([...intersectedEdges, ...smallPathAngleEdges, ...overlappingEdges])

    invalidEdges.forEach((edge) => {
      edge.isValid = false
    })

    return invalidEdges.size
  }

  /**
   * Groups edges from a wall mesh that are rendered connected
   * @param cycleBoundaries array of cycle boundaries used for filtering - ensuring that walls where cycle boundaries are close to mesh points are not returned as part of the connected edge group
   * @returns an array of wall meshes each containing only connected edges
   */
  public groupConnectedEdges(cycleBoundaries?: CycleBoundaryDrawable[]): WallMesh[] {
    let cycleBoundaryCenters: paper.Point[] = []
    if (cycleBoundaries) {
      cycleBoundaryCenters = cycleBoundaries.map((boundary) => boundary.getCenter())
    }

    const visited: Set<Edge> = new Set()
    const edgeGroups: Edge[][] = []
    const allEdges = this.getAllEdges()

    for (const edge of allEdges) {
      if (visited.has(edge)) {
        continue
      }

      // finding connected neighbors of edges recursively and adding them to the edge group
      const group = new Set<Edge>([
        edge,
        ...edge.getAllRecursiveNeighbours(allEdges, new Set<Edge>(), cycleBoundaryCenters),
      ])
      group.forEach((e) => visited.add(e))
      edgeGroups.push([...group])
    }

    const meshes: WallMesh[] = []
    for (const edgeGroup of edgeGroups) {
      const points = new Set<MeshPoint>([
        ...edgeGroup.map((e) => e.startPoint),
        ...edgeGroup.map((e) => e.endPoint),
      ])

      const mesh = new WallMesh(this.paperScope)
      mesh.points = points
      meshes.push(mesh)
    }

    return meshes
  }
}

const resetAnchorPoint = (
  anchorPoint: MeshPoint,
  removedEdge: Edge,
  corner: paper.Point
): paper.Point => {
  if (anchorPoint.edges.size !== 2) {
    return anchorPoint
  }
  const edges = Array.from(anchorPoint.edges)
  const neighbor = edges[0] === removedEdge ? edges[1] : edges[0]

  const angle = neighbor.getAngle(removedEdge)
  if (
    angle &&
    neighbor.thickness !== removedEdge.thickness &&
    Math.abs(angle) >= 180 - SNAP_ANGLE_INTERVAL / 2
  ) {
    return anchorPoint
  }

  const neighborDirection = neighbor.startPoint.subtract(neighbor.endPoint)
  const neighborNormal = new paper.Point(-neighborDirection.y, neighborDirection.x)

  const neighborCapLine = { start: corner, end: corner.add(neighborNormal) }
  const neighborCentralLine = {
    start: neighbor.startPoint,
    end: neighbor.endPoint,
  }

  return intersectLines(neighborCapLine, neighborCentralLine) ?? anchorPoint
}
